'use client';

import { useState, useMemo } from 'react';
import Link from 'next/link';
import { useQuery } from '@tanstack/react-query';
import { GraduationCap, Search, ChevronRight, Sparkles } from 'lucide-react';
import { splanApi } from '@/lib/splan-api';
import { useTranslations } from '@/i18n/I18nProvider';

export default function ProgramsPage() {
    const [search, setSearch] = useState('');
    const { t } = useTranslations('programs');

    const { data: programs, isLoading } = useQuery({
        queryKey: ['programs'],
        queryFn: () => splanApi.getPrograms(),
    });

    const filteredPrograms = programs?.filter(
        (program) =>
            program.name.toLowerCase().includes(search.toLowerCase()) ||
            program.shortName?.toLowerCase().includes(search.toLowerCase())
    );

    // Group by degree type with consistent ordering
    const groupedPrograms = useMemo(() => {
        if (!filteredPrograms) return {};
        const groups = filteredPrograms.reduce(
            (acc, program) => {
                const type = program.degreeType || 'other';
                if (!acc[type]) acc[type] = [];
                acc[type].push(program);
                return acc;
            },
            {} as Record<string, typeof programs>
        );
        // Sort programs within each group alphabetically
        for (const type of Object.keys(groups)) {
            groups[type]?.sort((a, b) => a.name.localeCompare(b.name));
        }
        return groups;
    }, [filteredPrograms]);

    const degreeTypeLabels: Record<string, string> = {
        bachelor: t('bachelorPrograms'),
        master: t('masterPrograms'),
        phd: t('phdPrograms'),
        other: t('otherPrograms'),
    };

    const degreeTypeOrder = ['bachelor', 'master', 'phd', 'other'];
    const orderedTypes = degreeTypeOrder.filter((type) => groupedPrograms[type]);

    // Suggested programs when search is empty
    const suggestedPrograms = useMemo(() => {
        if (!programs || search.trim()) return [];
        // Get first 6 programs (mix of bachelor and master if available)
        const bachelors = programs.filter((p) => p.degreeType === 'bachelor').slice(0, 4);
        const masters = programs.filter((p) => p.degreeType === 'master').slice(0, 2);
        return [...bachelors, ...masters];
    }, [programs, search]);

    const showSuggestions = !search.trim() && suggestedPrograms.length > 0;

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-[#0a0a0f]">
            {/* Sub-header */}
            <div className="bg-white dark:bg-white/[0.02] border-b border-gray-200 dark:border-white/5">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <h1 className="text-xl font-bold text-gray-900 dark:text-white/90 font-heading">
                        {t('studyPrograms')}
                    </h1>
                    <p className="text-sm text-gray-500 dark:text-white/50">
                        {t('browseAll')}
                    </p>
                </div>
            </div>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Search */}
                <div className="relative mb-8">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-white/40 w-5 h-5" />
                    <input
                        type="text"
                        placeholder={t('searchPlaceholder')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border border-gray-200 dark:border-white/10 rounded-lg bg-white dark:bg-white/5 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple/30 dark:focus:ring-purple-bright/30 focus:border-purple dark:focus:border-purple-bright/50"
                    />
                </div>

                {/* Suggestions when no search */}
                {showSuggestions && !isLoading && (
                    <div className="mb-8">
                        <div className="flex items-center gap-2 mb-4">
                            <Sparkles className="w-5 h-5 text-purple dark:text-purple-bright" />
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white/90 font-heading">
                                {t('popularPrograms')}
                            </h2>
                        </div>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {suggestedPrograms.map((program) => (
                                <ProgramCard key={program.id} program={program} />
                            ))}
                        </div>
                    </div>
                )}

                {/* Program List */}
                {isLoading ? (
                    <div className="text-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple dark:border-purple-bright mx-auto" />
                        <p className="mt-4 text-gray-500 dark:text-white/50">{t('loadingPrograms')}</p>
                    </div>
                ) : (
                    <div className="space-y-8">
                        {orderedTypes.map((type) => (
                            <div key={type}>
                                <h2 className="text-lg font-semibold text-gray-900 dark:text-white/90 mb-4 font-heading">
                                    {degreeTypeLabels[type] || type}
                                </h2>
                                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {groupedPrograms[type]?.map((program) => (
                                        <ProgramCard key={program.id} program={program} />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {filteredPrograms?.length === 0 && !isLoading && (
                    <div className="text-center py-12">
                        <GraduationCap className="w-12 h-12 text-gray-300 dark:text-white/20 mx-auto mb-4" />
                        <p className="text-gray-500 dark:text-white/50">{t('noPrograms')}</p>
                    </div>
                )}
            </main>
        </div>
    );
}

function ProgramCard({ program }: { program: { id: string; name: string; shortName: string } }) {
    const { data: semesters, isLoading } = useQuery({
        queryKey: ['semesters', program.id],
        queryFn: () => splanApi.getSemesters(program.id),
        staleTime: 10 * 60 * 1000, // 10 minutes
    });
    const { t } = useTranslations('programs');
    const { t: tCommon } = useTranslations('common');

    const hasSemesters = semesters && semesters.length > 0;

    return (
        <div className="bg-white dark:bg-white/[0.02] rounded-lg border border-gray-200 dark:border-white/5 overflow-hidden hover:border-purple/20 dark:hover:border-purple-bright/20 transition-colors">
            <div className="p-4">
                <div className="flex items-start gap-3">
                    <div className="p-2 bg-purple/10 dark:bg-purple-bright/15 rounded-lg">
                        <GraduationCap className="w-5 h-5 text-purple dark:text-purple-bright" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 dark:text-white/90 truncate">
                            {program.name}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-white/50">
                            {program.shortName}
                        </p>
                    </div>
                </div>
            </div>
            <div className="border-t border-gray-100 dark:border-white/5 p-2">
                {isLoading ? (
                    <div className="h-6 flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple dark:border-purple-bright" />
                    </div>
                ) : hasSemesters ? (
                    <div className="flex flex-wrap gap-1">
                        {semesters.map((semester) => (
                            <Link
                                key={semester.id}
                                href={`/programs/${program.id}/${semester.id}`}
                                className="text-xs px-2 py-1 bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-white/60 rounded hover:bg-purple/10 dark:hover:bg-purple-bright/15 hover:text-purple dark:hover:text-purple-bright transition-colors flex items-center gap-1"
                            >
                                {semester.number ? `${tCommon('semester')} ${semester.number}` : semester.name}
                                <ChevronRight className="w-3 h-3" />
                            </Link>
                        ))}
                    </div>
                ) : (
                    // Program without child semesters - treat it as a semester itself
                    <Link
                        href={`/programs/${program.id}/${program.id}`}
                        className="text-xs px-2 py-1 bg-purple/10 dark:bg-purple-bright/15 text-purple dark:text-purple-bright rounded hover:bg-purple/20 dark:hover:bg-purple-bright/25 transition-colors flex items-center gap-1 w-fit"
                    >
                        {t('viewTimetable')}
                        <ChevronRight className="w-3 h-3" />
                    </Link>
                )}
            </div>
        </div>
    );
}
